import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const API_DATE_FORMAT = 'YYYY/MM/D HH:mm:ss'
export const DISPLAY_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * Resolves the stored user preference value to an IANA timezone name.
 * The preference is stored either as a bare zone ("America/Detroit") or as
 * an offset-prefixed zone ("-0400 America/Detroit"). Falls back to the
 * browser zone when no preference is set.
 */
export const resolveTimezone = (preference: string | undefined | null): string => {
  if (!preference) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  if (preference[0] === '-' || preference[0] === '+') {
    const [, tz] = preference.split(' ')
    return tz || Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  return preference
}

/** Formats a UTC instant for display in the given zone. */
export const formatLocalTimeFromUtc = (
  date: string | Date | null | undefined,
  tz: string,
  format = DISPLAY_DATE_FORMAT
): string | null => {
  if (!date) return null

  try {
    const isoDate = dayjs.utc(date, 'YYYY-MM-DDTHH:mm:ssZ')
    if (isoDate.isValid()) {
      return isoDate.tz(tz).format(format)
    }

    return dayjs.utc(date).tz(tz).format(format)
  } catch (e) {
    console.error(`String incorrectly formatted ${date}`, e)
    return null
  }
}

/**
 * Converts an instant (Date) or a wall-clock string in the given zone to a
 * UTC string for the API.
 */
export const formatUtcTimeFromLocal = (
  date: string | Date | null | undefined,
  tz: string,
  format = API_DATE_FORMAT
): string | null => {
  if (!date) return null

  try {
    return dayjs.tz(date, tz).utc().format(format)
  } catch (e) {
    console.error(`String incorrectly formatted ${date}`, e)
    return null
  }
}

/**
 * DevExtreme pickers work in the browser zone. Given a UTC instant, returns a
 * Date whose browser-local wall clock equals the wall clock in `tz`, so the
 * picker displays the preferred zone's time.
 */
export const toPickerDate = (
  date: string | Date | null | undefined,
  tz: string
): Date | null => {
  if (!date) return null

  const wallClock = dayjs.utc(date).tz(tz).format('YYYY-MM-DDTHH:mm:ss')
  const picker = dayjs(wallClock)
  return picker.isValid() ? picker.toDate() : null
}

/**
 * Inverse of toPickerDate: takes a Date from a browser-zone picker, reads its
 * wall clock, and interprets that wall clock in `tz` to produce the real instant.
 */
export const fromPickerDate = (
  date: Date | null | undefined,
  tz: string
): Date | null => {
  if (!date) return null

  const wallClock = dayjs(date).format('YYYY-MM-DDTHH:mm:ss')
  return dayjs.tz(wallClock, tz).toDate()
}
